import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * Calculate the bottom position of the header component
 * @returns {number} Bottom position in pixels
 */
function getHeaderBottomPosition() {
  const header = document.querySelector('#header-component');
  if (!header) return 0;
  
  const headerRect = header.getBoundingClientRect();
  return headerRect.bottom;
}

/**
 * Update CSS custom properties for menu positioning
 */
function updateMenuPositioning() {
  const headerBottom = getHeaderBottomPosition();
  document.body.style.setProperty('--header-bottom-position', `${headerBottom}px`);
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {boolean} isDesktopDrawerOpen - Whether the desktop drawer is open.
 * @property {HTMLElement | null} activeDesktopDrawerItem - The currently active desktop drawer item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.#setupDesktopDrawer();

    window.addEventListener('pageswap', this.#cleanup);

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#cleanup();
    window.removeEventListener('pageswap', this.#cleanup);
  }

  #cleanup = () => {
    document.body.classList.remove('desktop-drawer-open');
  };

/**
 * @type {State}
 */
#state = {
  activeItem: null,
  isDesktopDrawerOpen: false,
  activeDesktopDrawerItem: null,
};  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Check if we should use desktop drawer mode
   * @returns {boolean}
   */
  get shouldUseDesktopDrawer() {
    return window.innerWidth >= 750 && this.hasAttribute('data-desktop-drawer');
  }

  /**
   * Get the desktop drawer container
   * @returns {HTMLElement | null}
   */
  get desktopDrawer() {
    return this.querySelector('.header-menu__desktop-drawer');
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(
      () => {
        item.removeAttribute('data-animating');
      },
      Math.max(0, this.animationDelay - 150)
    ); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Setup desktop drawer functionality
   */
  #setupDesktopDrawer = () => {
    if (!this.shouldUseDesktopDrawer) return;

    // Setup click listeners for menu items with submenus
    this.#setupMenuClickListeners();

    // Setup escape key and backdrop listeners
    document.addEventListener('keydown', this.#handleKeyDown, {
      signal: this.#abortController.signal,
    });

    window.addEventListener('resize', this.#handleResize, {
      signal: this.#abortController.signal,
    });

    // Update positioning on scroll for sticky headers
    window.addEventListener('scroll', updateMenuPositioning, {
      passive: true,
      signal: this.#abortController.signal,
    });

    // Initial positioning update
    updateMenuPositioning();

    this.#setupBackdropListener();

    // Setup event delegation for collapsible groups
    this.#setupCollapsibleEventDelegation();
  };

  /**
   * Setup click listeners for menu items
   */
  #setupMenuClickListeners = () => {
    const menuItems = this.querySelectorAll('.menu-list__list-item[data-has-submenu]');
    
    menuItems.forEach((item, index) => {
      const link = item.querySelector('.menu-list__link');
      if (link) {
        link.addEventListener('click', this.#handleMenuClick, {
          signal: this.#abortController.signal,
        });
      }
    });
  };

  /**
   * Handle menu item click for desktop drawer
   * @param {Event} event
   */
  #handleMenuClick = (event) => {
    if (!this.shouldUseDesktopDrawer) return;
    if (!(event.target instanceof Element)) return;

    const menuLink = event.target.closest('.menu-list__link');
    if (!menuLink) return;

    const menuListItem = menuLink.closest('.menu-list__list-item');
    if (!menuListItem || !(menuListItem instanceof HTMLElement)) return;
    if (!menuListItem.hasAttribute('data-has-submenu')) return;

    event.preventDefault();

    const menuIndex = parseInt(menuListItem.getAttribute('data-menu-index') || '0', 10);

    if (this.#state.activeDesktopDrawerItem === menuListItem && this.#state.isDesktopDrawerOpen) {
      this.#closeDesktopDrawer();
    } else {
      this.#openDesktopDrawer(menuListItem, menuIndex);
    }
  };

  /**
   * Open desktop drawer
   * @param {HTMLElement} menuItem
   * @param {number} menuIndex
   */
  #openDesktopDrawer = (menuItem, menuIndex) => {
    const drawer = this.desktopDrawer;
    if (!drawer) return;

    // Update positioning before opening
    updateMenuPositioning();

    // Hide all sections first
    const allSections = drawer.querySelectorAll('.desktop-drawer-section');
    allSections.forEach(section => {
      if (section instanceof HTMLElement) {
        section.style.display = 'none';
      }
    });

    // Show the relevant section
    const targetSection = drawer.querySelector(`[data-menu-item="${menuIndex}"]`);
    if (targetSection instanceof HTMLElement) {
      targetSection.style.display = 'block';
    }

    const header = document.querySelector('#header-component');
    if (header && header.dataset.stickyState === 'idle') {
      header.dataset.stickyState = 'active';
      header.removeAttribute('data-animating');
    }

    drawer.classList.add('header-menu__desktop-drawer--open');
    document.body.classList.add('desktop-drawer-open');

    this.#state.isDesktopDrawerOpen = true;
    this.#state.activeDesktopDrawerItem = menuItem;
    this.querySelectorAll('.menu-list__list-item').forEach(item => {
      item.classList.remove('menu-list__list-item--active');
    });
    menuItem.classList.add('menu-list__list-item--active');

    // Setup collapsible groups
    this.#setupCollapsibleGroups();
  };

  /**
   * Close desktop drawer
   */
  #closeDesktopDrawer = () => {
    const drawer = this.desktopDrawer;
    if (!drawer) return;

    const header = document.querySelector('#header-component');
    if (header) {
      header._drawerClosedAt = Date.now();
    }

    drawer.classList.remove('header-menu__desktop-drawer--open');
    document.body.classList.remove('desktop-drawer-open');
    this.#state.isDesktopDrawerOpen = false;
    this.#state.activeDesktopDrawerItem = null;

    // Remove active class from menu items
    this.querySelectorAll('.menu-list__list-item').forEach(item => {
      item.classList.remove('menu-list__list-item--active');
    });
  };

  /**
   * Setup event delegation for collapsible groups (more reliable)
   */
  #setupCollapsibleEventDelegation = () => {
    const drawer = this.desktopDrawer;
    if (!drawer) return;

    // Use event delegation on the drawer container
    drawer.addEventListener('click', (/** @type {Event} */ event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      
      // Check if clicked element is a group header
      if (target.classList.contains('desktop-drawer-group-header')) {
        event.preventDefault();
        event.stopPropagation();
        
        const group = target.closest('.desktop-drawer-group');
        if (group instanceof HTMLElement) {
          const isExpanded = group.classList.contains('desktop-drawer-group--expanded');
          
          if (!isExpanded) {
            const allGroups = drawer.querySelectorAll('.desktop-drawer-group');
            const allHeaders = drawer.querySelectorAll('.desktop-drawer-group-header');
            
            allGroups.forEach(otherGroup => {
              if (otherGroup !== group) {
                otherGroup.classList.remove('desktop-drawer-group--expanded');
                const ul = otherGroup.querySelector('ul');
                if (ul) {
                  ul.style.maxHeight = '';
                }
              }
            });
            
            allHeaders.forEach(otherHeader => {
              if (otherHeader !== target) {
                otherHeader.setAttribute('aria-expanded', 'false');
              }
            });
          }          

          if (isExpanded) {
            group.classList.remove('desktop-drawer-group--expanded');
            const ul = group.querySelector('ul');
            if (ul) {
              ul.style.maxHeight = '';
            }
            target.setAttribute('aria-expanded', 'false');
          } else {
            group.classList.add('desktop-drawer-group--expanded');
            target.setAttribute('aria-expanded', 'true');
            const ul = group.querySelector('ul');
            if (ul) {
              ul.style.maxHeight = ul.scrollHeight + 'px';
            }
          }
        }
      }
    }, {
      signal: this.#abortController.signal,
    });
  };

  /**
   * Setup collapsible groups for 4th level menu items (fallback)
   */
  #setupCollapsibleGroups = () => {
    // This method is now called as a fallback but event delegation should handle most cases
  };

  /**
   * Setup backdrop click listener
   */
  #setupBackdropListener = () => {
    const backdrop = this.desktopDrawer?.querySelector('.header-menu__desktop-drawer-backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        this.#closeDesktopDrawer();
      }, {
        signal: this.#abortController.signal,
      });
    }
  };

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#state.isDesktopDrawerOpen) {
      this.#closeDesktopDrawer();
    }
  };

  /**
   * Handle window resize
   */
  #handleResize = () => {
    if (!this.shouldUseDesktopDrawer && this.#state.isDesktopDrawerOpen) {
      this.#closeDesktopDrawer();
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
